import { useState, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { Box, TextField, FormControl, Grid, Autocomplete, Checkbox, FormControlLabel, useTheme } from "@mui/material";
import MapsAutofill from "../../components/MapsAutofill";
import { useSelector, useDispatch } from 'react-redux';
import { setAddress as updateAddress, setInStateForCare as updateInStateForCare } from '../../redux/userSlice';
import { MinorUserContext } from "../../context/UserNameProvider";
import { continueButtonSx, inputLabelSx, textFieldSx } from "../../lib/uiformatting";
import Question from "../../components/Question";
import ContinueButton from '../../components/ContinueButton';
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function Address() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const availableStates = ["CA", "CT", "FL", "MA", "NH", "WA"];
    const { isMinor, firstName } = useContext(MinorUserContext);
    const user = useSelector((state) => state.user);
    const theme = useTheme();

    const [address, setAddress] = useState(user.address);
    const [addressError, setAddressError] = useState({ line1: "", city: "", state: "", zip: "" });
    const [stateInput, setStateInput] = useState(user.address.state);
    const [inStateForCare, setInStateForCare] = useState(user.inStateForCare);

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/anything-else', navigate);
    };

    return (
        <Question
            onBack={handleBack}
            title="Please enter your full address."
            subtitle={`Our doctors will need it if any prescriptions are required for ${isMinor ? (firstName + "'s") : "your"} care`}
            input={
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <FormControl sx={{ width: "100%", mb: "2rem" }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <MapsAutofill
                                    label="Address Line 1"
                                    value={address.line1}
                                    defaultValue={{ description: address.line1 }}
                                    states={availableStates}
                                    onChange={(e) => {
                                        setAddressError({
                                            ...addressError,
                                            line1: e.target.value === "" ? "This field is required." : ""
                                        });
                                        setAddress({ ...address, line1: e.target.value });
                                    }}
                                    onSelect={(value) => {
                                        setAddress({
                                            ...address,
                                            line1: value.structured_formatting.main_text,
                                            city: value.terms[2].value,
                                            state: value.terms[3].value,
                                            zip: value.zip,
                                        });
                                    }}
                                    sx={{ ...textFieldSx }}
                                    error={addressError.line1.length !== 0}
                                    helperText={addressError.line1}

                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        ...textFieldSx,
                                        "& .MuiInputLabel-root": {
                                            backgroundColor: theme.palette.background.paper,
                                            padding: "0 32px",
                                            transition: "all 0.2s ease-in-out",
                                        },
                                    }}
                                    label="Address Line 2"
                                    variant="outlined"
                                    color="secondary"
                                    fullWidth
                                    value={address.line2}
                                    onChange={(e) => {
                                        setAddress({ ...address, line2: e.target.value });
                                    }}
                                    InputLabelProps={{
                                        sx: {
                                            ...inputLabelSx,
                                            "& .MuiInputLabel-shrink": {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        ...textFieldSx,
                                        "& .MuiInputLabel-root": {
                                            backgroundColor: theme.palette.background.paper,
                                            padding: "0 32px",
                                            transition: "all 0.2s ease-in-out",
                                        },
                                    }}
                                    label="City"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    color="secondary"
                                    value={address.city}
                                    onChange={(e) => {
                                        setAddressError({
                                            ...addressError,
                                            city: e.target.value === "" ? "This field is required." : ""
                                        });
                                        setAddress({ ...address, city: e.target.value });
                                    }}
                                    error={!!addressError.city}
                                    helperText={addressError.city}
                                    InputLabelProps={{
                                        sx: {
                                            ...inputLabelSx,
                                            "& .MuiInputLabel-shrink": {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    id="state-select"
                                    fullWidth
                                    inputValue={stateInput}
                                    onInputChange={(e, newInputValue) => {
                                        setStateInput(newInputValue);
                                    }}
                                    value={address.state}
                                    onChange={(e, newValue) => {
                                        setAddress({ ...address, state: newValue });
                                        setAddressError({
                                            ...addressError,
                                            state: !newValue ? "This field is required." : ""
                                        });
                                    }}
                                    options={availableStates}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="State"
                                            required
                                            error={addressError.state.length !== 0}
                                            helperText={addressError.state}
                                            sx={{
                                                ...textFieldSx,
                                                "& .MuiOutlinedInput-input": {
                                                    pt: 0,
                                                    pb: 0,
                                                    pl: 1,
                                                },
                                                "& .MuiInputLabel-root": {
                                                    backgroundColor: theme.palette.background.paper,
                                                    padding: "0 32px",
                                                    transition: "all 0.2s ease-in-out",
                                                },
                                            }}
                                            InputLabelProps={{
                                                sx: {
                                                    ...inputLabelSx,
                                                    "& .MuiInputLabel-shrink": {
                                                        backgroundColor: theme.palette.background.paper,
                                                    },
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} >
                                <TextField
                                    label="ZIP Code"
                                    required
                                    type="number"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    sx={{
                                        ...textFieldSx,
                                        "& .MuiInputLabel-root": {
                                            backgroundColor: theme.palette.background.paper,
                                            padding: "0 32px",
                                            transition: "all 0.2s ease-in-out",
                                        },
                                    }}
                                    variant="outlined"
                                    color="secondary"
                                    value={address.zip}
                                    onChange={(e) => {
                                        if (e.target.value === "") {
                                            setAddressError({ ...addressError, zip: "This field is required." });
                                        } else if (e.target.value.length !== 5) {
                                            setAddressError({ ...addressError, zip: "Please enter a valid ZIP code." });
                                        } else {
                                            setAddressError({ ...addressError, zip: "" });
                                        }
                                        setAddress({ ...address, zip: e.target.value });
                                    }}
                                    error={!!addressError.zip}
                                    helperText={addressError.zip}
                                    InputLabelProps={{
                                        sx: {
                                            ...inputLabelSx,
                                            "& .MuiInputLabel-shrink": {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={inStateForCare}
                                            onChange={(e) => {
                                                setInStateForCare(e.target.checked);
                                                dispatch(updateInStateForCare(e.target.checked));
                                            }}
                                        />
                                    }
                                    label="I will be in this state for my care"

                                />
                            </Grid>
                        </Grid>
                    </FormControl>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={() => {
                            if (process.env.REACT_APP_OURS_EVENT) {
                                // eslint-disable-next-line no-undef
                                ours('track', 'AddressEntry', { email: user.email, phone_number: user.phoneNo });
                                // eslint-disable-next-line no-undef
                                ours('track', 'Subscription', { value: 119, currency: 'USD' }, { email: user.email, phone_number: user.phoneNo });
                            }
                            dispatch(updateAddress(address));
                            setPageAndDeferNavigate(dispatch, '/hear-abt', navigate);
                        }}
                        disabled={
                            !address.line1 ||
                            !address.city ||
                            !address.state ||
                            address.zip.length !== 5 ||
                            !inStateForCare
                        }
                        title="Continue"
                        selected={
                            address.line1 &&
                            address.city &&
                            address.state &&
                            address.zip.length === 5 &&
                            inStateForCare
                        }
                    />
                </Box>
            }
        />
    );
}
